import { useState, useEffect } from 'react'
import type { FC } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment, Box, Grid, TextField, Typography } from '@mui/material'

import { LocationTypeSearchCategory, SpecialtyGroupSearchCategory } from './ProviderSearchContent'
import { ClinicalFocusArea, SpecialtyDropdownOption } from '~/entities/steerage'
import { CPTCode } from './types'
import { PlaceOfService, usePlaceOfServices, useProcedureSuggestionQuery } from './utils'

interface FacilitySearchOptionsPickerProps {
  locationTypes: SpecialtyDropdownOption[]
  onSelect: (
    locationType: LocationTypeSearchCategory | null,
    specialtyGroup: SpecialtyGroupSearchCategory | null,
    npi: string | null,
    providerName: string | null,
    gender: string | null,
    facilityName: string | null,
    procedureCode: CPTCode | null,
    cptCode: CPTCode | null,
    clinicalFocusAreas?: ClinicalFocusArea[],
    placeOfServices?: PlaceOfService[],
  ) => void
  disabled: boolean
  loading: boolean
  selectedLocationType: LocationTypeSearchCategory | null
  selectedFacilityName: string | null
  setSearchRadius: (val: number) => void
  selectedCPTCode: CPTCode | null
  selectedPlaceOfServices?: PlaceOfService[]
}

const LIMIT = 20

const ProcedureSearchOptionsPicker: FC<FacilitySearchOptionsPickerProps> = (
  props: FacilitySearchOptionsPickerProps,
) => {
  const [selectedOption, setSelectedOption] = useState<CPTCode | null>(props?.selectedCPTCode)
  const [debouncedSearchString, setDebouncedSearchString] = useState('')
  const [searchString, setSearchString] = useState<string>('')
  const [selectedPlaceOfServices, setSelectedPlaceOfServices] = useState<PlaceOfService[]>(
    props?.selectedPlaceOfServices ?? [],
  )

  // Fetch place of service data
  const { data: placeOfServices = [], isPending: isLoadingPlaceOfServices } = usePlaceOfServices()

  // Use react-query for procedure suggestion search
  const searchPayload =
    debouncedSearchString.length >= 3
      ? {
          search_query: debouncedSearchString,
          limit: LIMIT,
        }
      : null

  const { data: searchResults = [], isPending: isSearching } =
    useProcedureSuggestionQuery(searchPayload)

  useEffect(() => {
    if (searchString.length < 3) return
    const timer = setTimeout(() => {
      setDebouncedSearchString(searchString)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchString])

  // The selections follow the parent's whenever it changes them (e.g. from the URL).
  const [parentCPTCode, setParentCPTCode] = useState(props.selectedCPTCode)
  if (props.selectedCPTCode !== parentCPTCode) {
    setParentCPTCode(props.selectedCPTCode)
    setSelectedOption(props.selectedCPTCode)
  }
  const [parentPlaceOfServices, setParentPlaceOfServices] = useState(props.selectedPlaceOfServices)
  if (props.selectedPlaceOfServices !== parentPlaceOfServices) {
    setParentPlaceOfServices(props.selectedPlaceOfServices)
    setSelectedPlaceOfServices(props.selectedPlaceOfServices ?? [])
  }

  const handleInputChange = (event: React.SyntheticEvent, newSearchString: string) => {
    setSearchString(newSearchString)
    // Below the search threshold there is no request to throttle, so stale options clear at once.
    if (newSearchString.length < 3) setDebouncedSearchString(newSearchString)
  }

  const handleChange = (event: React.SyntheticEvent, newValue: CPTCode | null) => {
    setSelectedOption(newValue)
    props.onSelect(
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      newValue,
      undefined,
      selectedPlaceOfServices,
    )
    props?.setSearchRadius(25)
  }

  const handlePlaceOfServiceChange = (event: React.SyntheticEvent, newValue: PlaceOfService[]) => {
    setSelectedPlaceOfServices(newValue)
    props.onSelect(null, null, null, null, null, null, null, selectedOption, undefined, newValue)
  }

  /**
   * Essential for fuzzy search to work! Without this, Material-UI Autocomplete
   * applies its own default filtering which overrides our backend fuzzy search results.
   * This tells Autocomplete: "Don't filter - the API already provided perfect results!"
   */
  const filterOptions = (options: CPTCode[]) => {
    // Return all options as-is since API already provides filtered and ranked results
    return options
  }

  return (
    <Box style={{ gap: 20 }} sx={{ display: 'flex', maxWidth: 960, flexDirection: 'column' }}>
      <Grid size={6}>
        <Autocomplete
          value={selectedOption}
          onChange={handleChange}
          inputValue={searchString}
          onInputChange={handleInputChange}
          id="procedure-picker"
          options={searchResults}
          getOptionLabel={option => option.display || option.code || ''}
          sx={{ width: '100%' }}
          size="small"
          disabled={props.disabled}
          loading={props.loading || isSearching}
          forcePopupIcon={false}
          renderOption={({ key: _, ...props }, option: CPTCode | null) => (
            <li {...props} key={option?.id}>
              <Typography component="div">
                {option?.display ? <Box sx={{ fontWeight: '400' }}> {option?.display}</Box> : ''}
                {option?.code ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {'CPT: '}
                    {option?.code}
                  </Typography>
                ) : (
                  ''
                )}
                {option?.description && option?.description != 'undefined' && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {option.description}
                  </Typography>
                )}
              </Typography>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Procedure"
              placeholder="Type 3 or more characters to search..."
              variant="outlined"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          filterOptions={filterOptions}
        />
      </Grid>
      <Grid size={6}>
        <Autocomplete
          multiple
          value={selectedPlaceOfServices}
          onChange={handlePlaceOfServiceChange}
          id="place-of-service-picker"
          options={placeOfServices}
          getOptionLabel={option => `${option.code || ''} - ${option.label || ''}`}
          sx={{ width: '100%' }}
          size="small"
          disabled={props.disabled}
          loading={props.loading || isLoadingPlaceOfServices}
          renderOption={({ key: _, ...props }, option: PlaceOfService) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">
                  <strong>{option.code}</strong> - {option.label}
                </Typography>
                {option.description && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {option.description}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Place of Service (Optional)"
              placeholder="Select place of service..."
              variant="outlined"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                      {params.slotProps.input.startAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
    </Box>
  )
}

export default ProcedureSearchOptionsPicker
